import React, {useCallback, useContext, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {UpdateLocationOptionsType} from 'components/data/WithUrlUpdater'
import {DestinationPickerPopover} from 'components/DestinationPickerPopover'
import {addDays} from 'date-fns/addDays'
import {format} from 'date-fns/format'
import {
  OpenedFormControl,
  RecentSearchParams,
  useSearchBox,
  useSearchBoxDatePicker,
  useSearchBoxDestinationPicker,
  useSearchBoxGuestPicker
} from 'hooks/searchBox'
import {useDestinationDisplayName} from 'hooks/useDestinationDisplayName'
import {getMeta} from 'modules/meta/selectors'
import {
  getSearchParameters,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getIsMultiRoomBundlesSearch} from 'modules/search/selectors'
import {
  getOpenDatePickerType,
  getSearchBoxValues,
  getShowRoomsLabel
} from 'modules/searchBox/selectors'
import {SearchBoxValues, SearchTrigger} from 'modules/searchBox/types'
import {getUserSearchHistory} from 'utils/persistence'

import {Box} from '@vio/atlas/helpers/Box'
import {OutsideClickHandler} from '@vio/atlas/helpers/OutsideClickHandler'
import {PopoverPlacement} from '@vio/atlas/Popover'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {TrackEventPayload} from '@vio/core/src/analytics/types/Events'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import {ConfigureI18nContext} from '@vio/core/src/localization/components/ConfigureI18n'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {
  numberOfGuests,
  roomsSplitToString,
  roomsToConfigurationObject
} from '@vio/core/src/room/business/roomConfiguration'
import {
  RoomConfigType,
  RoomSplitType
} from '@vio/core/src/room/types/RoomConfiguration'
import {WithColorPriceDayData} from '@vio/core/src/search/modules/ColorPriceCalendar/components/WithColorPriceDayData'
import {useColorPriceCalendar} from '@vio/core/src/search/modules/ColorPriceCalendar/hooks'
import {dateToMiddayDate, UTS_DATE_FORMAT} from '@vio/core/src/utils/date'
import {POPOVER_VERTICAL_OFFSET} from '@vio/shared/src/search/constants'
import {DatePickerPopover} from '@vio/shared/src/search/DatePickerPopover'
import {GuestRoomConfigPopover} from '@vio/shared/src/search/GuestRoomConfig/GuestRoomConfigPopover'
import {SearchFormHorizontal} from '@vio/shared/src/search/SearchFormHorizontal'
import {PoweredBy} from '@vio/shared/src/whiteLabel/PoweredBy'
import {SearchFormHorizontalFullWidth} from '@vio/shared/src/whiteLabel/SearchFormHorizontalFullWidth'

const SearchBoxWrapperBase = styled.div`
  background: ${cssTheme.colors.background.neutral.primary};
  border-radius: ${cssTheme.layout.radius.rounded};
`

const InputError = styled(Text)`
  padding-block: ${cssTheme.layout.spacing.s300} 0;
  padding-inline: ${cssTheme.layout.spacing.s500} 0;
`

const isScrollBarClicked = (e: MouseEvent, offset = 20) =>
  e.clientX + offset >= document.documentElement.offsetWidth

interface Props {
  searchOptions?: UpdateLocationOptionsType
  additionalSearchParams?: Partial<SearchBoxValues>
}

export const SearchFormDesktop: React.FC<Props> = ({
  searchOptions,
  additionalSearchParams = {homeSearch: '1'}
}) => {
  const containerRef = useRef<HTMLDivElement>()
  const guestPickerRef = useRef<HTMLDivElement>()
  const destinationInputRef = useRef<HTMLDivElement>()
  const [openedFormControl, setOpenedFormControl] = useState<OpenedFormControl>(
    OpenedFormControl.NONE
  )
  const [isGeolocationSearchSelected, setIsGeolocationSearchSelected] =
    useState(false)

  const {shell, commerce} = useBrandCapabilities()
  const isCoBranded = commerce.coBranded.enabled
  const hasAlternativeSearchForm = shell.hasAlternativeSearchForm.enabled
  const alternativeSearchFormSettings = shell.hasAlternativeSearchForm.enabled
    ? shell.hasAlternativeSearchForm.config
    : undefined
  const {formatMessage} = useIntl()

  const {hasFormTitle, messages: alternativeSearchFormMessages} =
    alternativeSearchFormSettings || {}
  const userSearchHistory = getUserSearchHistory()
  const latestUserSearch = userSearchHistory?.[0]
  const today = dateToMiddayDate(new Date())
  const tomorrow = addDays(today, 1)
  const todayString = format(today, UTS_DATE_FORMAT)
  const tomorrowString = format(tomorrow, UTS_DATE_FORMAT)
  const initialDatePickerValues = {
    checkIn: latestUserSearch?.searchParams.checkIn || todayString,
    checkOut: latestUserSearch?.searchParams.checkOut || tomorrowString
  }

  const initialGuestPickerValues = {
    rooms: latestUserSearch?.searchParams.rooms,
    optimizeRooms: latestUserSearch
      ? latestUserSearch.searchParams.optimizeRooms
      : true
  }

  const {doSearch, onSearchButtonClick} = useSearchBox()
  const {
    destinationHasError,
    onDestinationChange,
    onDestinationPickerInputClick,
    onRecentSearchSelected,
    setDestinationHasError,
    destinationDisplayValue,
    destinationPickerInputDisplayValue,
    onSuggestionsRequested,
    onNearbyPropertiesSuggestionSelected
  } = useSearchBoxDestinationPicker()
  const {
    onDateChange,
    onDatePickerClose,
    onDatePickerDayClick,
    onDatePickerOpen,
    onDatePickerInputClick,
    onDatePickerSubmit,
    onDatePickerReset
  } = useSearchBoxDatePicker(initialDatePickerValues)
  const {
    onGuestRoomsChange,
    onGuestOptimizeRoomsChange,
    onGuestPickerClick,
    onGuestPickerSubmit,
    onGuestPickerReset,
    onGuestPickerDisplayed,
    onGuestPickerOptionInfoDisplayed,
    onGuestConfigPreferenceClick
  } = useSearchBoxGuestPicker(initialGuestPickerValues)

  const {
    checkIn,
    checkOut,
    hotelId,
    placeId,
    rooms,
    address,
    optimizeRooms,
    iataCode
  } = useSelector(getSearchBoxValues)
  const searchBoxDestinationDisplayValue = useDestinationDisplayName()

  const showTotalPrices = useSelector(getShowTotalPrices)
  const searchParameters = useSelector(getSearchParameters)
  const combinedSearchParameters = {
    ...searchParameters,
    rooms,
    checkIn,
    checkOut
  }
  const {includeLocalTaxes, includeTaxes} = useSelector(getMeta)
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch)
  const pricingContext = {
    includeTaxes,
    includeLocalTaxes,
    includeRoomsInNightlyPrice: isMultiRoomBundlesSearch
  }
  const numberOfGuestsValue = numberOfGuests(rooms)

  const isColorPriceDatePickerEnabled =
    numberOfGuestsValue <= 2 && Boolean(hotelId)

  const openedDatePickerType = useSelector(getOpenDatePickerType)
  const {monthNames, weekDayNamesShort, firstDayOfWeek} =
    useContext(ConfigureI18nContext)
  const {numberOfAdults, numberOfChildren, numberOfRooms, roomsSplit} =
    roomsToConfigurationObject(rooms)
  const showRoomsLabel = useSelector(getShowRoomsLabel)

  const isDestinationPickerOpen = useMemo(
    () => openedFormControl === OpenedFormControl.DESTINATION,
    [openedFormControl]
  )

  const isCheckInPickerOpen = useMemo(
    () => openedFormControl === OpenedFormControl.CHECK_IN,
    [openedFormControl]
  )
  const isCheckOutPickerOpen = useMemo(
    () => openedFormControl === OpenedFormControl.CHECK_OUT,
    [openedFormControl]
  )

  const isGuestPickerOpen = useMemo(
    () => openedFormControl === OpenedFormControl.GUEST,
    [openedFormControl]
  )

  const dispatchTeamEvent = useDispatchTeamEvent()
  const trackEventCallback = useCallback(
    (payload: TrackEventPayload) => dispatchTeamEvent(trackEvent(payload)),
    [dispatchTeamEvent]
  )

  const {requestOneMonthAvailability} = useColorPriceCalendar({
    searchParameters: combinedSearchParameters,
    pricingContext
  })

  const handleRecentSearchSelected = useCallback(
    (recentSearch: RecentSearchParams): void => {
      onRecentSearchSelected({
        recentSearch,
        additionalSearchParams
      })
    },
    [onRecentSearchSelected]
  )

  const handleSuggestionSelected = useCallback((): void => {
    setIsGeolocationSearchSelected(false)
    setOpenedFormControl(OpenedFormControl.CHECK_IN)
    onDatePickerOpen(DatePickerType.CheckIn)
  }, [onDatePickerOpen, setIsGeolocationSearchSelected])

  const handleNearbyPropertiesSuggestionSelected = useCallback((): void => {
    onNearbyPropertiesSuggestionSelected(false)
    setIsGeolocationSearchSelected(true)
    setOpenedFormControl(OpenedFormControl.CHECK_IN)
    onDatePickerOpen(DatePickerType.CheckIn)
  }, [
    onNearbyPropertiesSuggestionSelected,
    setIsGeolocationSearchSelected,
    onDatePickerOpen
  ])

  const handleDestinationClick = useCallback((): void => {
    setOpenedFormControl(OpenedFormControl.DESTINATION)
    setDestinationHasError(false)
    onDestinationPickerInputClick()
  }, [
    setDestinationHasError,
    onDestinationPickerInputClick,
    setIsGeolocationSearchSelected
  ])

  const handleCloseDestinationPicker = (e: MouseEvent): void => {
    // do not close the destination picker if we are clicking within the input container or scrollbar
    if (
      !isScrollBarClicked(e) &&
      !destinationInputRef?.current?.contains(e.target as Node)
    ) {
      handleCloseAllPopovers()
    }
  }

  const handleCheckInClick = useCallback((): void => {
    onDatePickerInputClick(DatePickerType.CheckIn)
    onDatePickerOpen(DatePickerType.CheckIn)
    setOpenedFormControl(OpenedFormControl.CHECK_IN)
  }, [onDatePickerInputClick, onDatePickerOpen])

  const handleCheckOutClick = useCallback((): void => {
    onDatePickerInputClick(DatePickerType.CheckOut)
    onDatePickerOpen(DatePickerType.CheckOut)
    setOpenedFormControl(OpenedFormControl.CHECK_OUT)
  }, [onDatePickerInputClick, onDatePickerOpen])

  const handleCloseDatePicker = useCallback((): void => {
    onDatePickerClose()
  }, [onDatePickerClose])

  const handleSubmitDatePicker = useCallback((): void => {
    handleCloseAllPopovers()
    onDatePickerSubmit()
  }, [onDatePickerSubmit])

  const handleGuestPickerClick = useCallback((): void => {
    setOpenedFormControl(OpenedFormControl.GUEST)
    onGuestPickerClick()
    onGuestPickerDisplayed()
  }, [onGuestPickerClick, onGuestPickerDisplayed])

  const handleCloseAllPopovers = () =>
    setOpenedFormControl(OpenedFormControl.NONE)

  const handleCloseGuestPicker = () => {
    setOpenedFormControl(OpenedFormControl.NONE)
  }

  const handleGuestRoomsChange = useCallback(
    (roomsSplit: RoomSplitType[]): void => {
      const rooms = roomsSplitToString(roomsSplit)
      onGuestRoomsChange(rooms)
    },
    [onGuestRoomsChange]
  )

  const handleGuestOptimizeRoomsChange = useCallback(
    (isOptimizeRooms: boolean): void => {
      onGuestOptimizeRoomsChange(isOptimizeRooms)
    },
    [onGuestOptimizeRoomsChange]
  )

  const handleSubmitGuestPicker = useCallback((): void => {
    handleCloseAllPopovers()
    onGuestPickerSubmit()
  }, [onGuestPickerSubmit])

  const handleSubmit = useCallback(
    (event?: React.SyntheticEvent | null | undefined): void | boolean => {
      event?.preventDefault()
      handleCloseAllPopovers()

      if (
        !placeId &&
        !hotelId &&
        !isGeolocationSearchSelected &&
        !address &&
        !iataCode
      ) {
        setDestinationHasError(true)
        return false
      }

      // to have the DatePicker closed on the search page
      onDatePickerClose()

      onSearchButtonClick()

      doSearch({
        newUrlParams: additionalSearchParams,
        searchTrigger: SearchTrigger.Submit,
        searchOptions
      })
    },
    [
      onDatePickerClose,
      doSearch,
      onSearchButtonClick,
      setDestinationHasError,
      searchBoxDestinationDisplayValue,
      placeId,
      hotelId,
      isGeolocationSearchSelected,
      address
    ]
  )

  const handleOutsideClick = (e: MouseEvent): void => {
    /** Preventing from closing the popovers by clicking on the scrollbar or if no form controls are open */
    if (
      !isScrollBarClicked(e) &&
      openedFormControl !== OpenedFormControl.NONE
    ) {
      handleCloseAllPopovers()
      onDatePickerReset()
      onGuestPickerReset()
    }
  }

  const handleDatePickerDayClick = (name: DatePickerType, date: string) => {
    onDatePickerDayClick(name, date)

    setOpenedFormControl(
      name === DatePickerType.CheckIn
        ? OpenedFormControl.CHECK_OUT
        : OpenedFormControl.CHECK_IN
    )
  }

  const getSearchFormComponent = () => {
    if (hasAlternativeSearchForm) {
      return SearchFormHorizontalFullWidth
    }

    return SearchFormHorizontal
  }

  const SearchFormComponent = getSearchFormComponent()

  return (
    <>
      <Box display="flex" flexDirection="column" gap="s400">
        {hasFormTitle && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text variant="titleL">
              {formatMessage(alternativeSearchFormMessages.formTitle)}
            </Text>
            {isCoBranded && <PoweredBy />}
          </Box>
        )}
        <SearchBoxWrapperBase ref={containerRef}>
          <OutsideClickHandler
            display="contents"
            onOutsideClick={handleOutsideClick}
          >
            <SearchFormComponent
              checkIn={checkIn}
              checkOut={checkOut}
              destinationButtonDisplayValue={destinationDisplayValue}
              destinationInputDisplayValue={destinationPickerInputDisplayValue}
              numberOfAdults={numberOfAdults}
              numberOfChildren={numberOfChildren}
              numberOfRooms={numberOfRooms}
              onDestinationPickerClick={handleDestinationClick}
              onCheckInDatePickerClick={handleCheckInClick}
              onCheckOutDatePickerClick={handleCheckOutClick}
              onGuestPickerClick={handleGuestPickerClick}
              onSubmit={handleSubmit}
              isDestinationPickerOpen={isDestinationPickerOpen}
              isCheckInPickerOpen={isCheckInPickerOpen}
              isCheckOutPickerOpen={isCheckOutPickerOpen}
              isGuestPickerOpen={isGuestPickerOpen}
              guestPickerRef={guestPickerRef}
              destinationInputRef={destinationInputRef}
              onSuggestionsRequested={onSuggestionsRequested}
              destinationHasError={destinationHasError}
              isRoomsLabelShown={showRoomsLabel}
              trackEvent={trackEventCallback}
              isHomePage
            />
            <DestinationPickerPopover
              isOpen={isDestinationPickerOpen}
              anchorRef={containerRef}
              onChange={onDestinationChange}
              onSuggestionSelected={handleSuggestionSelected}
              onRecentSearchSelected={handleRecentSearchSelected}
              onClose={handleCloseDestinationPicker}
              verticalOffset={POPOVER_VERTICAL_OFFSET}
              onNearbyPropertiesSuggestionSelected={
                handleNearbyPropertiesSuggestionSelected
              }
            />
            <DatePickerPopover
              isOpen={isCheckInPickerOpen || isCheckOutPickerOpen}
              openedDatePickerType={openedDatePickerType}
              anchorRef={containerRef}
              checkIn={checkIn}
              checkOut={checkOut}
              onClose={handleCloseDatePicker}
              onChange={onDateChange}
              onDatePickerOpen={onDatePickerOpen}
              onDatePickerDayClick={handleDatePickerDayClick}
              weekdaysShort={weekDayNamesShort}
              months={monthNames}
              firstDayOfWeek={firstDayOfWeek}
              onSubmit={handleSubmitDatePicker}
              submitButtonMessage={
                <FormattedMessageWrapper id="apply" defaultMessage="Apply" />
              }
              showTotalPrices={showTotalPrices}
              shouldShowPrices
              onMonthView={date => requestOneMonthAvailability(date, [hotelId])}
              isColorPriceDatePickerEnabled={isColorPriceDatePickerEnabled}
              DayInnerContainer={WithColorPriceDayData}
              verticalOffset={POPOVER_VERTICAL_OFFSET}
            />
            <GuestRoomConfigPopover
              isOpen={isGuestPickerOpen}
              anchorRef={guestPickerRef}
              placement={PopoverPlacement.Left}
              roomsSplit={roomsSplit}
              onRoomsChange={handleGuestRoomsChange}
              onOptimizeRoomsChange={handleGuestOptimizeRoomsChange}
              onClose={handleCloseGuestPicker}
              onSubmit={handleSubmitGuestPicker}
              onInfoOpen={onGuestPickerOptionInfoDisplayed}
              onGuestConfigOptionClick={onGuestConfigPreferenceClick}
              verticalOffset={
                hasAlternativeSearchForm
                  ? POPOVER_VERTICAL_OFFSET
                  : POPOVER_VERTICAL_OFFSET + 4
              }
              submitButtonMessage={
                <FormattedMessageWrapper id="apply" defaultMessage="Apply" />
              }
              roomConfigType={
                optimizeRooms ? RoomConfigType.Optimized : RoomConfigType.Manual
              }
            />
          </OutsideClickHandler>
        </SearchBoxWrapperBase>
      </Box>
      {destinationHasError && (
        <InputError colorPath="content.danger.secondary" variant="bodyXS">
          {searchBoxDestinationDisplayValue ? (
            <FormattedMessageWrapper
              id="destinationPickerUnknownMessage"
              defaultMessage="Sorry, we don't recognize that location"
            />
          ) : (
            <FormattedMessageWrapper
              id="destinationPickerEmptyMessage"
              defaultMessage="Tell us where you would like to go"
            />
          )}
        </InputError>
      )}
    </>
  )
}
